import {Paper, Select, Text} from "@mantine/core";
import {useMemo, useState} from "react";
import {useEntityTypes} from "../../../../utils/api.ts";
import type {ShipType, VehicleType} from "../../../../types/swc.types.ts";
import style from "./ProductionCargoCarriers.module.css";

type Carrier = ShipType | VehicleType;

// Mammoth, BFF, Modular Taskforce Cruiser, Colossal
const STANDARD_CARRIER_IDS = ['2:77', '2:30', '2:75', '2:253'];

function formatNumber(value: number): string {
    return value.toLocaleString(undefined, {maximumFractionDigits: 2});
}

function CarrierCard({carrier, weight, volume}: {carrier: Carrier, weight: number, volume: number}) {
    const weightTrips = weight / carrier.weightcap;
    const volumeTrips = volume / carrier.volumecap;
    const trips = Math.max(1, Math.ceil(Math.max(weightTrips, volumeTrips)));
    return (
        <Paper withBorder p="xs" className={style.carrier}>
            <img src={carrier.images.small} alt={carrier.name} className={style.carrierImg}/>
            <Text fw={600} size="sm" className={style.carrierName}>{carrier.name}</Text>
            <Text size="xs" c="dimmed">{formatNumber(carrier.weightcap)} T · {formatNumber(carrier.volumecap)} m³</Text>
            {'hyper' in carrier.speed ? <Text size="xs" c="dimmed">Hyper: {carrier.speed.hyper}</Text> : null}
            <Text className={style.trips}>{formatNumber(trips)} <span className={style.tripsLabel}>{trips === 1 ? 'trip' : 'trips'}</span></Text>
            <Text size="xs" c="dimmed">Limited by {weightTrips >= volumeTrips ? 'weight' : 'volume'}</Text>
        </Paper>
    );
}

export default function ProductionCargoCarriers({weight, volume}: {weight: number, volume: number}) {
    const [carrierId, setCarrierId] = useState<string | null>(null);
    const {ships, vehicles} = useEntityTypes(['ships', 'vehicles']);

    const carrierOptions = useMemo(() => [
        {group: 'Ships', items: ships},
        {group: 'Vehicles', items: vehicles},
    ].map((group) => ({
        group: group.group,
        items: group.items
            .filter((carrier) => carrier.weightcap > 0 && carrier.volumecap > 0)
            .map((carrier) => ({value: carrier.id, label: carrier.name}))
            .sort((left, right) => left.label.localeCompare(right.label)),
    })), [ships, vehicles]);
    const carriers = useMemo(() => {
        const all: Carrier[] = [...ships, ...vehicles];
        const ids = carrierId && !STANDARD_CARRIER_IDS.includes(carrierId) ? [...STANDARD_CARRIER_IDS, carrierId] : STANDARD_CARRIER_IDS;
        return ids.map((id) => all.find((entry) => entry.id === id)).filter((entry): entry is Carrier => !!entry);
    }, [ships, vehicles, carrierId]);

    return (
        <>
            <div className={style.carriers}>
                {carriers.map((carrier) => (
                    <CarrierCard key={carrier.id} carrier={carrier} weight={weight} volume={volume}/>
                ))}
            </div>
            <Select placeholder="Compare another ship or vehicle" searchable clearable
                    data={carrierOptions} value={carrierId} onChange={setCarrierId}/>
            <Text size="xs" c="dimmed">Trips assume one carrier at full capacity with materials split across loads.</Text>
        </>
    );
}
